import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Calendar, CreditCard, Tag, Receipt, Check, X, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import UpsellModal from '../components/UpsellModal';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Cart() {
  const { items, removeItem, updateQuantity, clearCart, total } = useCart();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [orderType, setOrderType] = useState('immediate');
  const [reservationDate, setReservationDate] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // New states
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [requestInvoice, setRequestInvoice] = useState(false);
  const [hasBillingInfo, setHasBillingInfo] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  // Check if user has billing info
  useEffect(() => {
    if (user) {
      checkBillingInfo();
      // Load user's registration coupon if available
      if (user.registration_coupon && !appliedCoupon) {
        setCouponCode(user.registration_coupon);
      }
    }
  }, [user]);

  const checkBillingInfo = async () => {
    try {
      const response = await fetch(`${API}/billing`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHasBillingInfo(!!data);
      }
    } catch (error) {
      console.error('Error checking billing info:', error);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    try {
      const response = await fetch(`${API}/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: couponCode.trim(),
          cart_total: total
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppliedCoupon(data);
        toast.success(`Cupón aplicado: -$${data.discount_amount.toFixed(2)}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Cupón no válido');
      }
    } catch (error) {
      toast.error('Error al validar cupón');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const calculateFinalTotal = () => {
    if (appliedCoupon) {
      return appliedCoupon.new_total;
    }
    return total;
  };

  const handleProceedToCheckout = () => {
    if (!user) {
      login();
      return;
    }

    if (items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }

    if (orderType === 'reservation' && !reservationDate) {
      toast.error('Selecciona una fecha para tu apartado');
      return;
    }

    // Show upsell modal before checkout
    setShowUpsell(true);
  };

  const handleCheckout = async () => {
    setShowUpsell(false);
    setLoading(true);

    try {
      // Create order
      const orderPayload = {
        items: items,
        order_type: orderType,
        reservation_date: reservationDate ? format(reservationDate, 'yyyy-MM-dd') : null,
        request_invoice: requestInvoice,
        coupon_code: appliedCoupon?.coupon?.code || null
      };

      const orderResponse = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(orderPayload)
      });

      if (!orderResponse.ok) {
        throw new Error('Error al crear el pedido');
      }

      const orderData = await orderResponse.json();

      // Create checkout session
      const checkoutResponse = await fetch(`${API}/orders/${orderData.order_id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Error al procesar el pago');
      }

      const checkoutData = await checkoutResponse.json();
      
      // Update coupon uses if applied
      if (appliedCoupon?.coupon?.coupon_id) {
        // This will be handled server-side when payment succeeds
      }
      
      // Clear cart and redirect to Stripe
      clearCart();
      window.location.href = checkoutData.checkout_url;

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error al procesar tu pedido');
    } finally {
      setLoading(false);
    }
  };

  const finalTotal = calculateFinalTotal();
  const discountAmount = appliedCoupon ? appliedCoupon.discount_amount : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/menu">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-stone-900">Tu Carrito</h1>
              <p className="text-stone-600">{items.length} {items.length === 1 ? 'producto' : 'productos'}</p>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-10 h-10 text-stone-400" />
              </div>
              <h2 className="text-xl font-semibold text-stone-900 mb-2">Tu carrito está vacío</h2>
              <p className="text-stone-500 mb-6">Agrega productos desde nuestro menú</p>
              <Link to="/menu">
                <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="go-to-menu-btn">
                  Ver Menú
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map(item => (
                  <div
                    key={item.product_id}
                    className="bg-white rounded-2xl border border-stone-100 p-4 flex gap-4"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                      <img
                        src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-stone-900 mb-1">{item.name}</h3>
                      <p className="text-orange-500 font-bold">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="text-stone-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* User's Registration Coupon Banner */}
                {user?.registration_coupon && !appliedCoupon && (
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-4 text-white flex items-center gap-4">
                    <Ticket className="w-8 h-8 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold">¡Tienes un cupón de bienvenida!</p>
                      <p className="text-emerald-100 text-sm">Usa tu código {user.registration_coupon} para 10% de descuento</p>
                    </div>
                    <Button 
                      size="sm"
                      className="bg-white text-emerald-700 hover:bg-emerald-50"
                      onClick={() => {
                        setCouponCode(user.registration_coupon);
                        applyCoupon();
                      }}
                    >
                      Aplicar
                    </Button>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-28">
                  <h2 className="text-lg font-semibold text-stone-900 mb-6">Resumen del Pedido</h2>
                  
                  {/* Order Type Selection */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-stone-700 mb-3">Tipo de pedido</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setOrderType('immediate')}
                        className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                          orderType === 'immediate'
                            ? 'border-emerald-700 bg-emerald-50 text-emerald-700'
                            : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                        }`}
                        data-testid="order-type-immediate"
                      >
                        <ShoppingBag className="w-5 h-5 mx-auto mb-1" />
                        Inmediato
                      </button>
                      <button
                        onClick={() => setOrderType('reservation')}
                        className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                          orderType === 'reservation'
                            ? 'border-emerald-700 bg-emerald-50 text-emerald-700'
                            : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                        }`}
                        data-testid="order-type-reservation"
                      >
                        <Calendar className="w-5 h-5 mx-auto mb-1" />
                        Apartado
                      </button>
                    </div>
                  </div>

                  {/* Date Picker for Reservation */}
                  {orderType === 'reservation' && (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-stone-700 mb-3">Fecha de entrega</p>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal border-stone-200 rounded-xl"
                            data-testid="date-picker-trigger"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {reservationDate ? (
                              format(reservationDate, "PPP", { locale: es })
                            ) : (
                              <span className="text-stone-500">Selecciona una fecha</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarPicker
                            mode="single"
                            selected={reservationDate}
                            onSelect={setReservationDate}
                            disabled={(date) => date < addDays(new Date(), 1)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  {/* Coupon Code */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-stone-700 mb-3">Código de descuento</p>
                    {appliedCoupon ? (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <Tag className="w-5 h-5 text-emerald-600" />
                        <div className="flex-1">
                          <p className="font-medium text-emerald-700">{appliedCoupon.coupon.code}</p>
                          <p className="text-xs text-emerald-600">-${appliedCoupon.discount_amount.toFixed(2)}</p>
                        </div>
                        <button onClick={removeCoupon} className="text-stone-400 hover:text-red-500">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ingresa tu código"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="flex-1 rounded-xl"
                          data-testid="coupon-input"
                        />
                        <Button 
                          variant="outline" 
                          onClick={applyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="rounded-xl"
                          data-testid="apply-coupon-btn"
                        >
                          {couponLoading ? '...' : 'Aplicar'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Invoice Request */}
                  {user && (
                    <div className="mb-6">
                      <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={requestInvoice}
                          onChange={(e) => {
                            if (e.target.checked && !hasBillingInfo) {
                              toast.error('Primero configura tus datos de facturación en Ajustes');
                              navigate('/settings/billing');
                              return;
                            }
                            setRequestInvoice(e.target.checked);
                          }}
                          className="w-5 h-5 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid="request-invoice-checkbox"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-stone-500" />
                            <span className="font-medium text-stone-900">Requiero factura</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5">
                            {hasBillingInfo ? 'Se generará automáticamente' : 'Configura tus datos primero'}
                          </p>
                        </div>
                        {requestInvoice && <Check className="w-5 h-5 text-emerald-600" />}
                      </label>
                    </div>
                  )}

                  {/* Totals */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-stone-600">
                      <span>Subtotal</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Descuento</span>
                        <span>-${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-stone-600">
                      <span>Envío</span>
                      <span className="text-emerald-600">Gratis</span>
                    </div>
                    <div className="h-px bg-stone-200 my-3"></div>
                    <div className="flex justify-between text-lg font-bold text-stone-900">
                      <span>Total</span>
                      <span>${finalTotal.toFixed(2)} MXN</span>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 text-lg font-medium"
                    onClick={handleProceedToCheckout}
                    disabled={loading}
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        Procesando...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        {user ? 'Continuar al Pago' : 'Iniciar Sesión para Pagar'}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Upsell Modal */}
      <UpsellModal
        isOpen={showUpsell}
        onClose={() => setShowUpsell(false)}
        onProceed={handleCheckout}
        cartProductIds={items.map(i => i.product_id)}
      />
    </div>
  );
}
